import { useEffect, useState, useRef, useCallback } from 'react';
import { X, ExternalLink, Loader2 } from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from './editor';
import { apiGet } from '../lib/api';

interface MeetingDrawerInstance {
  id: string;
  series_id: string | null;
  title: string | null;
  series_name: string | null;
  meeting_date: string;
  note_content: string | null;
}

interface MeetingDrawerProps {
  instanceId: string | null;
  onClose: () => void;
}

const MIN_HEIGHT = 150;
const DEFAULT_HEIGHT = Math.round(window.innerHeight * 0.4);
const MAX_HEIGHT_RATIO = 0.8;

export function MeetingDrawer({ instanceId, onClose }: MeetingDrawerProps) {
  const [instance, setInstance] = useState<MeetingDrawerInstance | null>(null);
  const [loading, setLoading] = useState(false);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  // Fetch meeting data when instanceId changes
  useEffect(() => {
    if (!instanceId) {
      setInstance(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    apiGet<MeetingDrawerInstance>(`/meeting-instances/${instanceId}`)
      .then((data) => {
        if (!cancelled) setInstance(data);
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [instanceId]);

  const maxHeight = Math.round(window.innerHeight * MAX_HEIGHT_RATIO);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setIsDragging(true);
      dragStartY.current = e.clientY;
      dragStartHeight.current = height;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [height],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      // Dragging up increases height, dragging down decreases
      const delta = dragStartY.current - e.clientY;
      const newHeight = Math.max(0, Math.min(maxHeight, dragStartHeight.current + delta));
      setHeight(newHeight);
    },
    [isDragging, maxHeight],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      setIsDragging(false);
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);

      // If dragged below minimum, close the drawer
      const delta = dragStartY.current - e.clientY;
      const finalHeight = dragStartHeight.current + delta;
      if (finalHeight < MIN_HEIGHT) {
        onClose();
        // Reset height for next open
        setHeight(DEFAULT_HEIGHT);
      }
    },
    [isDragging, onClose],
  );

  const isOpen = instanceId !== null;

  // Don't render anything when closed
  if (!isOpen) return null;

  const noteContent: JSONContent | null =
    instance?.note_content
      ? (() => {
          try {
            return JSON.parse(instance.note_content!);
          } catch {
            return null;
          }
        })()
      : null;

  const displayTitle = instance?.title || instance?.series_name || 'Untitled Meeting';

  const handleOpenInNewTab = () => {
    if (!instance) return;
    const path = instance.series_id
      ? `/meetings/${instance.series_id}/${instance.id}`
      : `/meetings/standalone/${instance.id}`;
    window.open(path, '_blank');
  };

  return (
    <div
      ref={drawerRef}
      className={`absolute bottom-0 left-0 right-0 z-30 bg-bg-secondary border-t border-border shadow-lg transition-transform duration-200 ease-out ${
        isDragging ? '!transition-none' : ''
      }`}
      style={{
        transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
        height: isOpen ? `${height}px` : 0,
      }}
    >
      {/* Drag handle */}
      <div
        className="flex items-center justify-center py-1.5 cursor-ns-resize select-none touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div className="w-10 h-1 rounded-full bg-text-muted/40" />
      </div>

      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-1.5 border-b border-border">
        {loading ? (
          <Loader2 size={14} className="text-text-muted animate-spin" />
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-text-primary truncate block">
                {displayTitle}
              </span>
              {instance && (
                <span className="text-[10px] text-text-muted">
                  {formatShortDate(instance.meeting_date)}
                </span>
              )}
            </div>
            <button
              onClick={handleOpenInNewTab}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={14} />
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
              title="Close"
            >
              <X size={14} />
            </button>
          </>
        )}
      </div>

      {/* Content — read-only editor */}
      <div className="flex-1 overflow-y-auto" style={{ height: `calc(100% - 68px)` }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="text-text-muted animate-spin" />
          </div>
        ) : noteContent ? (
          <div className="px-4 py-2">
            <DossierEditor
              content={noteContent}
              onUpdate={() => {}}
              editable={false}
              placeholder=""
            />
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-sm text-text-muted">
            No notes for this meeting
          </div>
        )}
      </div>
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
